import { memo, FocusEvent, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FormikErrors } from 'formik';
import { AttributeTypes } from '@iddataweb/idw-common-ui/enums';
import { getAXNUIAttributeLabel } from "@iddataweb/idw-common-ui/utils";
import { AttributeFormState } from '@idw-types/state/attribute-form-state';
import { getLanguageContent } from '@redux/selectors/language-selectors';
import {
	isHiddenAttribute
} from '@utils/attribute-input-util';
import { GenericInputComponent } from '../generic-input-component/generic-input-component';
import { GenericInputLabel } from '../generic-input-label/generic-input-label';
import styles from "./generic-attribute-container.less";

type GenericAttributeContainerProps = {
	attribute: AttributeFormState;
	localizeStrings: boolean;
	fieldName: string;
	fieldTouched: boolean;
	fieldError: string;
	fieldIsInvalid: boolean;
	fieldShouldBeHighlighted: boolean;
	pinFieldShouldBeAutofocused: boolean;
	attributeValuesPinIndex: number;
	setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
	onBlur: (event: FocusEvent<HTMLInputElement>) => void;
    handleAddNewSubmitValue: (payload: { attribute: AttributeFormState; inputData: any }) => void;
    handleUpdateSubmitValue: (payload: { attribute: AttributeFormState; inputData: any }) => void;
    handleUpdatePickerValue: (payload: { attribute: AttributeFormState; pickerData: any }) => void;
	isSubmitting: boolean;
};

export const GenericAttributeContainer = memo((props: GenericAttributeContainerProps) => {
	const {
		attribute,
		localizeStrings,
		fieldName,
		fieldTouched,
		fieldError,
		fieldIsInvalid,
		fieldShouldBeHighlighted,
		pinFieldShouldBeAutofocused,
		attributeValuesPinIndex,
		setFieldTouched,
		setFieldValue,
		onBlur,
		handleAddNewSubmitValue,
		handleUpdatePickerValue,
		handleUpdateSubmitValue,
		isSubmitting
	} = props;

	const content = useSelector(getLanguageContent);
	const shouldHideAttribute = isHiddenAttribute(attribute.type);

	const attributeLabel = useMemo(() => {
		return getAXNUIAttributeLabel(
			localizeStrings,
			content.labels.attributes[attribute.type],
			attribute.label
		)
	}, [
		localizeStrings,
		content.labels.attributes[attribute.type],
		attribute.label
	]);

	const isRadioGroup = useMemo(() => {
		return [
			AttributeTypes.PIN_DELIVERY_PREFERENCE,
			AttributeTypes.EMAIL_OR_PHONE_PIN_PREFERENCE
		].includes(attribute.type);
	}, [attribute.type]);

	const inputGroupContent = useMemo(() => {
		return (
			<>
				{!shouldHideAttribute && (
					<GenericInputLabel
						attribute={attribute}
						attributeLabel={attributeLabel}
						content={content}
						isRadioGroup={isRadioGroup}
					/>
				)}
				<GenericInputComponent
					attribute={attribute}
					attributeValuesPinIndex={attributeValuesPinIndex}
					attributeLabel={attributeLabel}
					fieldError={fieldError}
					fieldIsInvalid={fieldIsInvalid}
					fieldName={fieldName}
					fieldShouldBeHighlighted={fieldShouldBeHighlighted}
					pinFieldShouldBeAutofocused={pinFieldShouldBeAutofocused}
					fieldTouched={fieldTouched}
					handleAddNewSubmitValue={handleAddNewSubmitValue}
					handleUpdatePickerValue={handleUpdatePickerValue}
					handleUpdateSubmitValue={handleUpdateSubmitValue}
					isSubmitting={isSubmitting}
					onBlur={onBlur}
					setFieldTouched={setFieldTouched}
					setFieldValue={setFieldValue}
				/>
			</>
		);
	}, [
		shouldHideAttribute,
		attribute,
		attributeValuesPinIndex,
		attributeLabel,
		content,
		isRadioGroup,
		fieldError,
		fieldIsInvalid,
		fieldName,
		fieldShouldBeHighlighted,
		pinFieldShouldBeAutofocused,
		fieldTouched,
		handleAddNewSubmitValue,
		handleUpdatePickerValue,
		handleUpdateSubmitValue,
		isSubmitting,
		onBlur,
		setFieldTouched,
		setFieldValue
	]);

	return (
		<div
			id={`HTMLattr${attribute.id}`}
			className={styles.genericAttributeContainer}
			style={{ display: attribute.isVisible ? "block" : "none" }}
		>
			{isRadioGroup ? (
				<fieldset role={"radiogroup"} aria-live="polite">
					{inputGroupContent}
				</fieldset>
			) : (
				<span role={"region"} aria-live="polite">
					{inputGroupContent}
				</span>
			)}
		</div >
	);
});